/**
 * Services overview page
 * - Showcases Digital Marketing and Media Planning & Buying services
 * - Adds non-visual SEO meta and page-specific FAQ JSON-LD
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../components/ui/button'
import { TrendingUp, Target, ArrowRight } from 'lucide-react'
import Seo from '../components/Seo'

/**
 * Services page component with SEO
 */
export default function ServicesPage(): JSX.Element {
  /**
   * FAQ JSON-LD for the Services page
   */
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is included in Edicoo\'s media planning services?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our media planning services include audience research, channel selection, schedule planning, negotiation with publishers and post-campaign analysis to optimize reach and ROI.'
        }
      },
      {
        '@type': 'Question',
        name: 'Does Edicoo handle both digital and traditional media?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — Edicoo provides end-to-end support for both digital channels (social, search, programmatic) and traditional channels (TV, radio, print, OOH).'
        }
      },
      {
        '@type': 'Question',
        name: 'Can Edicoo run influencer campaigns?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — we manage influencer selection, contracts, content briefs, publishing and performance tracking across Kurdish and Arabic markets.'
        }
      }
    ]
  }

  return (
    <>
      <Seo
        title="Services — Edicoo"
        description="Edicoo offers digital marketing, influencer marketing, media planning & buying, content creation and campaign monitoring across Iraq & Kurdistan."
        url="https://your-site.com/services"
        image="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/15c08900-9eb7-4dd0-b764-47bc928699b3.jpg"
        jsonLd={[faqJsonLd]}
      />

      <div>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-orange-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-lg text-orange-100 max-w-3xl mx-auto leading-relaxed">
              Our adept team analyses various channels and advertising mediums to determine the most efficient way to connect your message with the intended audience. Through in-depth research and scrutiny, we identify the ideal platforms and moments to ensure your message reaches the right people at the right time.
            </p>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

              {/* Digital Marketing Services */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-orange-100 p-3 rounded-lg mr-4">
                    <TrendingUp className="h-8 w-8 text-orange-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Digital Marketing</h2>
                </div>

                <p className="text-gray-600 mb-8 leading-relaxed">
                  Modern digital solutions to reach and engage your target audience across all digital platforms.
                </p>

                <div className="space-y-4 mb-8">
                  <Link
                    to="/services/influencer-marketing"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Influencer Marketing</h3>
                      <p className="text-sm text-gray-600">Strategic partnerships with key influencers</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/paid-advertising"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Paid Advertising</h3>
                      <p className="text-sm text-gray-600">Google Ads, Meta Ads, and more</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/social-media"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Social Media Management</h3>
                      <p className="text-sm text-gray-600">Complete social media strategy and execution</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/content-creation"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Content Creation</h3>
                      <p className="text-sm text-gray-600">Creative content across all formats</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/translation"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Translation & Adaptation</h3>
                      <p className="text-sm text-gray-600">Cultural adaptation and localization</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>
                </div>

                <Button asChild className="w-full bg-orange-600 hover:bg-orange-700">
                  <Link to="/services/digital-marketing">
                    View Digital Marketing Details
                  </Link>
                </Button>
              </div>

              {/* Media Planning & Buying Services */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-orange-100 p-3 rounded-lg mr-4">
                    <Target className="h-8 w-8 text-orange-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Media Planning & Buying</h2>
                </div>

                <p className="text-gray-600 mb-8 leading-relaxed">
                  Strategic media planning and buying across traditional channels for maximum reach and impact.
                </p>

                <div className="space-y-4 mb-8">
                  <Link
                    to="/services/tv-advertising"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">TV Advertising</h3>
                      <p className="text-sm text-gray-600">Strategic television campaign planning</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/ooh-advertising"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Out-of-Home Advertising</h3>
                      <p className="text-sm text-gray-600">Billboards, transit, and outdoor media</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/radio-advertising"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Radio Advertising</h3>
                      <p className="text-sm text-gray-600">Audio campaigns across radio platforms</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/print-advertising"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Print Advertising</h3>
                      <p className="text-sm text-gray-600">Newspaper and magazine campaigns</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>

                  <Link
                    to="/services/monitoring"
                    className="flex items-center justify-between p-4 bg-gray-50 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">Monitoring & Tracking</h3>
                      <p className="text-sm text-gray-600">Campaign performance analysis</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-orange-600" />
                  </Link>
                </div>

                <Button asChild className="w-full bg-orange-600 hover:bg-orange-700">
                  <Link to="/services/media-planning">
                    View Media Planning Details
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}